import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Badge, Button, Form, Modal } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type { ChangeFormField } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';

type SummaryRow = Pick<
  ChangeFormField,
  'id' | 'label' | 'kind' | 'native_key' | 'detail_summary_visible' | 'is_locked'
>;

interface RowProps {
  field: SummaryRow;
  onToggle: (id: number, visible: boolean) => void;
}

const SortableSummaryRow: React.FC<RowProps> = ({ field, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
      }}
      className="border rounded p-2 mb-2 bg-white"
    >
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <button
          type="button"
          className="btn btn-light btn-sm"
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
          aria-label={`Reordenar ${field.label}`}
          title="Segure e arraste para cima ou para baixo"
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </button>
        <div className="flex-grow-1">
          <strong>{field.label}</strong>
          <div className="small text-muted">{field.kind === 'native' ? 'Campo do sistema' : 'Campo extra'}</div>
        </div>
        {field.is_locked ? <Badge bg="secondary">sistema</Badge> : null}
        {!field.detail_summary_visible ? <Badge bg="warning">oculto no detalhe</Badge> : null}
        <Form.Check
          type="switch"
          id={`detail-summary-visible-${field.id}`}
          label="Mostrar no detalhe"
          checked={Boolean(field.detail_summary_visible)}
          onChange={(e) => onToggle(field.id, e.target.checked)}
        />
      </div>
    </div>
  );
};

interface Props {
  embedded?: boolean;
  modal?: boolean;
  show?: boolean;
  onHide?: () => void;
}

const ChangeDetailSummaryConfigurator: React.FC<Props> = ({
  embedded = false,
  modal = false,
  show = false,
  onHide,
}) => {
  const { showAlert } = useModal();
  const [rows, setRows] = useState<SummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await changesApi.getDetailSummarySchema();
      setRows(
        data.items.map((item) => ({
          id: item.id,
          label: item.label,
          kind: item.kind,
          native_key: item.native_key,
          detail_summary_visible: Boolean(item.detail_summary_visible),
          is_locked: Boolean(item.is_locked),
        })),
      );
      setDirty(false);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Falha ao carregar o resumo do detalhe.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (embedded || show) {
      void load();
    }
  }, [embedded, show, load]);

  const activeRow = useMemo(
    () => rows.find((row) => row.id === activeId) || null,
    [activeId, rows],
  );

  const onToggle = (id: number, visible: boolean) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, detail_summary_visible: visible } : row)),
    );
    setDirty(true);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) {
      return;
    }
    setRows((prev) => {
      const oldIndex = prev.findIndex((row) => row.id === active.id);
      const newIndex = prev.findIndex((row) => row.id === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return prev;
      }
      return arrayMove(prev, oldIndex, newIndex);
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await changesApi.saveDetailSummarySchema({
        items: rows.map((row) => ({
          id: row.id,
          detail_summary_visible: Boolean(row.detail_summary_visible),
        })),
      });
      setRows(applySavedRows(data.items));
      setDirty(false);
      window.dispatchEvent(new CustomEvent('change-detail-summary-saved'));
      showAlert({
        title: 'Resumo salvo',
        message: 'O card superior das mudanças foi atualizado. Se a mudança já estiver aberta, ela recarrega o resumo automaticamente.',
        variant: 'success',
      });
      if (modal) {
        onHide?.();
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Falha ao salvar o resumo do detalhe.'));
    } finally {
      setSaving(false);
    }
  };

  const applySavedRows = (items: ChangeFormField[]) =>
    items.map((item) => ({
      id: item.id,
      label: item.label,
      kind: item.kind,
      native_key: item.native_key,
      detail_summary_visible: Boolean(item.detail_summary_visible),
      is_locked: Boolean(item.is_locked),
    }));

  const body = (
    <>
      <p className="text-muted">
        Define o card superior ao abrir uma mudança (ex.: Mudança #28). Não altera a modal Nova mudança.
        O título da mudança continua fixo no topo; demais campos obedecem esta lista.
      </p>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!error && !loading && !dirty && !modal ? (
        <Alert variant="success" className="py-2">
          Configuração salva. Atualize a página da mudança para ver o card.
        </Alert>
      ) : null}
      {loading ? (
        <p className="text-muted mb-0">Carregando campos...</p>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(event) => setActiveId(Number(event.active.id))}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={rows.map((row) => row.id)} strategy={verticalListSortingStrategy}>
              {rows.map((field) => (
                <SortableSummaryRow key={field.id} field={field} onToggle={onToggle} />
              ))}
            </SortableContext>
            <DragOverlay>
              {activeRow ? (
                <div className="border rounded p-2 bg-white shadow-sm">
                  <strong>{activeRow.label}</strong>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </>
      )}
    </>
  );

  const footer = (
    <div className="d-flex flex-wrap gap-2">
      <Button type="button" onClick={() => void handleSave()} disabled={saving || loading || !dirty}>
        {saving ? 'Salvando...' : 'Salvar resumo do detalhe'}
      </Button>
      <Button
        type="button"
        variant="outline-secondary"
        onClick={() => void load()}
        disabled={saving || loading}
      >
        Recarregar
      </Button>
      {modal ? (
        <Button type="button" variant="secondary" onClick={onHide} disabled={saving}>
          Fechar
        </Button>
      ) : null}
    </div>
  );

  if (modal) {
    return (
      <Modal show={show} onHide={onHide} size="lg" scrollable backdrop={saving ? 'static' : true}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Resumo do detalhe da mudança</Modal.Title>
        </Modal.Header>
        <Modal.Body>{body}</Modal.Body>
        <Modal.Footer>{footer}</Modal.Footer>
      </Modal>
    );
  }

  if (embedded) {
    return (
      <div>
        <h4 className="mb-2">Resumo do detalhe</h4>
        {body}
        <div className="mt-3">{footer}</div>
      </div>
    );
  }

  return <div>{body}</div>;
};

export default ChangeDetailSummaryConfigurator;
